import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import type { Book } from '@/types'
import { supabase } from '../../lib/supabase'
import { useFileImport, type ParsedBook } from '../../hooks/useFileImport'
import BulkEditModal from '../BulkEditModal'
import ScannedBookDetailModal from '../ScannedBookDetailModal'
import { normalizeBookText } from '../../utils/textNormalization'
import { searchBookInfo } from '../../utils/googleBooks'
import { searchOpenLibraryInfo } from '../../utils/openLibrary'
import { FIELD_LIMITS, truncateField, normalizeToArray, validateAuthors } from '../../utils/validation'
import FetchReviewModal, { type FetchResult as FetchReviewResult } from '../FetchReviewModal'
import CustomConfirmModal from '../common/CustomConfirmModal'
import styles from './BookScanner.module.css'

interface DetectedBook extends ParsedBook {
  id?: string
  duplicateStatus?: 'exact' | 'partial' | 'new'
}

interface FetchResult {
  index: number
  currentBook: DetectedBook
  apiResults: APIResult[]
}

interface APIResult {
  source: 'google' | 'openlibrary'
  description?: string
  categories?: string[] | string
  page_count?: number | string
  publisher?: string
}

interface UpdateFields {
  description?: string
  category?: string[]
  page_count?: number | null
  publisher?: string
}

interface ApplyUpdate {
  index: number
  updatedFields: UpdateFields
}

interface NotificationState {
  isOpen: boolean
  title: string
  message: string
  type: 'success' | 'danger' | 'warning' | 'info'
}

export default function BookScanner() {
  const { t } = useTranslation()
  const [scanning, setScanning] = useState<boolean>(false)
  const [detectedBooks, setDetectedBooks] = useState<DetectedBook[]>([])
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set())
  const [existingBooks, setExistingBooks] = useState<Book[]>([])
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [isDraggingList, setIsDraggingList] = useState<boolean>(false)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [savingBooks, setSavingBooks] = useState<boolean>(false)
  const [checkingDuplicates, setCheckingDuplicates] = useState<boolean>(false)
  const [showBulkEditModal, setShowBulkEditModal] = useState<boolean>(false)
  const [notification, setNotification] = useState<NotificationState>({ isOpen: false, title: '', message: '', type: 'info' })

  const showNotify = (title: string, message: string, type: NotificationState['type'] = 'info') => {
    setNotification({ isOpen: true, title, message, type })
  }

  const [matchPreviewBook, setMatchPreviewBook] = useState<Book | null>(null)
  const [fetchingInfo, setFetchingInfo] = useState<boolean>(false)
  const [fetchResults, setFetchResults] = useState<FetchResult[] | null>(null)

  // Scanned Book Detail Modal State
  const [editingScannedBookIndex, setEditingScannedBookIndex] = useState<number | null>(null)
  const [editingScannedBook, setEditingScannedBook] = useState<DetectedBook | null>(null)

  const [existingAuthors, setExistingAuthors] = useState<string[]>([])
  const [existingPublishers, setExistingPublishers] = useState<string[]>([])
  const [existingLocations, setExistingLocations] = useState<string[]>([])
  const [existingSeries, setExistingSeries] = useState<string[]>([])
  const [existingCategories, setExistingCategories] = useState<string[]>([])
  const [existingLanguages, setExistingLanguages] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const listFileInputRef = useRef<HTMLInputElement>(null)

  // Use shared file import hook
  const { readAndParseFile } = useFileImport()

  // Check for existing books in database
  const checkExistingBooks = async () => {
    setCheckingDuplicates(true)
    const { data: { user } } = await supabase.auth.getUser()

    if (!user) {
      setCheckingDuplicates(false)
      return
    }

    // Fetch all user's books with full details
    const { data: userBooks, error } = await supabase
      .from('books')
      .select('*')
      .eq('user_id', user.id)

    if (error) {
      console.error('Error fetching existing books:', error)
      setCheckingDuplicates(false)
      return
    }

    setExistingBooks(userBooks || [])

    // Extract unique values for hybrid dropdowns
    const authors = [...new Set((userBooks || []).flatMap(b => b.author || []))].filter(Boolean).sort((a, b) => a.localeCompare(b))
    const publishers = [...new Set((userBooks || []).map(b => b.publisher).filter(Boolean))].sort((a, b) => a.localeCompare(b))
    const locations = [...new Set((userBooks || []).map(b => b.location).filter(Boolean))].sort((a, b) => a.localeCompare(b))
    const series = [...new Set((userBooks || []).map(b => b.series).filter(Boolean))].sort((a, b) => a.localeCompare(b))
    const categories = [...new Set((userBooks || []).flatMap(b => b.category || []))].filter(Boolean).sort((a, b) => a.localeCompare(b))
    const languages = [...new Set((userBooks || []).map(b => b.language).filter(Boolean))].sort((a, b) => a.localeCompare(b))
    setExistingAuthors(authors)
    setExistingPublishers(publishers)
    setExistingLocations(locations)
    setExistingSeries(series)
    setExistingCategories(categories)
    setExistingLanguages(languages)

    setCheckingDuplicates(false)
  }

  // Get match status for a book
  const getBookMatchStatus = (book) => {
    const bookTitle = book.title?.toLowerCase().trim() || ''

    for (const existing of existingBooks) {
      const existingTitle = existing.title?.toLowerCase().trim() || ''

      // Exact match (title + any author overlap)
      if (bookTitle === existingTitle) {
        const hasAuthorOverlap = Array.isArray(existing.author) && Array.isArray(book.author) &&
          existing.author.some(existingA =>
            book.author.some(importA =>
              existingA?.toLowerCase().trim() === importA?.toLowerCase().trim()
            )
          )

        if (hasAuthorOverlap) return 'exact'
      }
    }

    // Check for partial match (same title, different author)
    for (const existing of existingBooks) {
      const existingTitle = existing.title?.toLowerCase().trim() || ''

      if (bookTitle === existingTitle) {
        return 'partial'
      }
    }

    return 'none'
  }

  // Get the matching book for preview
  const getMatchingBook = (book) => {
    const bookTitle = book.title?.toLowerCase().trim() || ''

    for (const existing of existingBooks) {
      const existingTitle = existing.title?.toLowerCase().trim() || ''

      if (bookTitle === existingTitle) {
        return existing
      }
    }
    return null
  }

  const processImage = async (file) => {
    if (!file) return

    setScanning(true)
    setDetectedBooks([])
    setSelectedIndices(new Set())
    setExistingBooks([])

    // Create preview
    const previewUrl = URL.createObjectURL(file)
    setImagePreview(previewUrl)

    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = async () => {
      const base64 = (reader.result as string).split(',')[1]

      try {
        const response = await fetch(
          `${((import.meta as unknown) as { env: Record<string, string> }).env.VITE_SUPABASE_URL}/functions/v1/scan-books`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${((import.meta as unknown) as { env: Record<string, string> }).env.VITE_SUPABASE_ANON_KEY}`
            },
            body: JSON.stringify({ imageBase64: base64 })
          }
        )

        const data = await response.json()

        if (!response.ok) {
          console.error('Error de la función:', data)
          setScanning(false)
          return
        }

        if (data.books && data.books.length > 0) {
          // Normalize text (convert all-uppercase to title case)
          const b = FIELD_LIMITS.books;
          const normalizedBooks = data.books.map(book => {
            const normalized = normalizeBookText(book);
            const authorArray = validateAuthors(normalizeToArray(normalized.author));
            const cat = normalizeToArray(normalized.category);
            return {
              ...normalized,
              author: authorArray,
              notes: truncateField(normalized.notes, b.notes),
              synopsis: truncateField(normalized.synopsis, b.synopsis),
              description: truncateField(normalized.description, b.description),
              publisher: truncateField(normalized.publisher, b.publisher),
              location: truncateField(normalized.location, b.location),
              series: truncateField(normalized.series, b.series),
              category: cat,
              language: truncateField(normalized.language, b.language)
            };
          });
          setDetectedBooks(normalizedBooks)
          // Check for existing books after detection
          await checkExistingBooks()
        }
        setScanning(false)
      } catch (err) {
        console.error('Error:', err)
        setScanning(false)
      }
    }
  }

  const handleImageUpload = (event) => {
    const file = event.target.files[0]
    processImage(file)
  }

  const handleListFileUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    try {
      const data = await readAndParseFile(file)
      if (data.length > 0) {
        setDetectedBooks(prev => [...prev, ...data])
        await checkExistingBooks()
      }
    } catch (error) {
      showNotify(t('scanner.errors.importTitle'), error.message, "danger")
    }
    e.target.value = ''
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files[0]
    if (file && file.type.startsWith('image/')) {
      processImage(file)
    }
  }

  const handleListDragOver = (e) => {
    e.preventDefault()
    setIsDraggingList(true)
  }

  const handleListDragLeave = (e) => {
    e.preventDefault()
    setIsDraggingList(false)
  }

  const handleListDrop = async (e) => {
    e.preventDefault()
    setIsDraggingList(false)
    const file = e.dataTransfer.files[0]
    if (file) {
      try {
        const data = await readAndParseFile(file)
        if (data.length > 0) {
          setDetectedBooks(prev => [...prev, ...data])
          await checkExistingBooks()
        }
      } catch (error) {
        showNotify(t('scanner.errors.importTitle'), error.message, "danger")
      }
    }
  }

  // Toggle individual book selection
  const toggleBookSelection = (e, index) => {
    e.stopPropagation()
    setSelectedIndices(prev => {
      const newSet = new Set(prev)
      if (newSet.has(index)) {
        newSet.delete(index)
      } else {
        newSet.add(index)
      }
      return newSet
    })
  }

  // Select all books
  const selectAll = () => {
    setSelectedIndices(new Set<number>(detectedBooks.map((_, i) => i)))
  }

  // Deselect all books
  const deselectAll = () => {
    setSelectedIndices(new Set())
  }

  // Update book field
  const updateBookField = (index, field, value) => {
    setDetectedBooks(prev => {
      const b = FIELD_LIMITS.books;
      const processingMap = new Map([
        ['notes', v => truncateField(v, b.notes)],
        ['synopsis', v => truncateField(v, b.synopsis)],
        ['description', v => truncateField(v, b.description)],
        ['publisher', v => truncateField(v, b.publisher)],
        ['location', v => truncateField(v, b.location)],
        ['series', v => truncateField(v, b.series)],
        ['category', v => truncateField(v, b.category)],
        ['language', v => truncateField(v, b.language)]
      ]);

      const processor = processingMap.get(field);
      const truncatedValue = processor ? processor(value) : value;

      return prev.map((item, i) =>
        i === index ? { ...item, [field]: truncatedValue } : item
      );
    })
  }

  // Apply bulk edit to selected books
  const applyBulkEdit = (updates) => {
    setDetectedBooks(prev => {
      return prev.map((book, index) => {
        if (selectedIndices.has(index)) {
          return { ...book, ...updates }
        }
        return book
      })
    })
    setShowBulkEditModal(false)
  }

  // Save selected books with all fields
  // Remove single scanned book
  const removeScannedBook = (index) => {
    const newBooks = [...detectedBooks]
    newBooks.splice(index, 1)
    setDetectedBooks(newBooks)

    // Update selection indices
    const newSelection = new Set<number>()
    selectedIndices.forEach(selectedIndex => {
      if (selectedIndex < index) {
        newSelection.add(selectedIndex)
      } else if (selectedIndex > index) {
        newSelection.add(selectedIndex - 1)
      }
    })
    setSelectedIndices(newSelection)
  }

  // Bulk remove
  const handleBulkRemove = () => {
    const indicesToRemove = Array.from(selectedIndices).sort((a, b) => b - a)
    const newBooks = [...detectedBooks]

    indicesToRemove.forEach(index => {
      newBooks.splice(index, 1)
    })

    setDetectedBooks(newBooks)
    setSelectedIndices(new Set())
  }

  const handleScannedBookClick = (book, index) => {
    setEditingScannedBook(book)
    setEditingScannedBookIndex(index)
  }

  const handleSaveIndividualScannedBook = () => {
    // Book is already saved to DB by the modal
    // We just need to remove it from the scanned list
    if (editingScannedBookIndex !== null) {
      removeScannedBook(editingScannedBookIndex)
    }
  }

  const handleUpdateScannedBook = (updatedBook) => {
    // Update local state without saving to database
    if (editingScannedBookIndex !== null) {
      setDetectedBooks(prev =>
        prev.map((book, i) => i === editingScannedBookIndex ? updatedBook : book)
      )
    }
  }

  const handleFetchInfo = async () => {
    if (selectedIndices.size === 0) return
    setFetchingInfo(true)

    try {
      const indices = Array.from(selectedIndices)
      const results = []

      // Process requests in parallel
      await Promise.all(indices.map(async (index) => {
        const book = detectedBooks[index]
        // Only search if we have a title to search for
        if (book?.title) {
          const [googleInfo, olInfo] = await Promise.all([
            searchBookInfo(book.title, book.author).catch(() => null),
            searchOpenLibraryInfo(book.title, book.author).catch(() => null)
          ])

          const apiResults = []
          if (googleInfo) apiResults.push({ source: 'google', ...googleInfo })
          if (olInfo) apiResults.push({ source: 'openlibrary', ...olInfo })

          if (apiResults.length > 0) {
            results.push({
              index,
              currentBook: book,
              apiResults
            })
          }
        }
      }))

      if (results.length > 0) {
        // Show review modal instead of applying directly
        const sortedResults = [...results].sort((a, b) => a.index - b.index)
        setFetchResults(sortedResults)
      } else {
        // Maybe show a toast or alert if no info found?
        // For now, simple console log or no-op
        console.log('No info found for selected books')
      }
    } catch (error) {
      console.error('Error fetching book info:', error)
    } finally {
      setFetchingInfo(false)
    }
  }

  const handleApplyFetchResults = (updates: ApplyUpdate[]) => {
    // updates: array of { index, updatedFields }
    if (!updates || updates.length === 0) {
      setFetchResults(null)
      return
    }

    const updatesMap = new Map(updates.map(u => [u.index, u.updatedFields]));

    setDetectedBooks(prev => {
      const b = FIELD_LIMITS.books;
      return prev.map((book, i) => {
        const updatedFields = updatesMap.get(i);
        if (updatedFields) {
          return {
            ...book,
            description: truncateField(updatedFields.description || book.description, b.description),
            category: updatedFields.category || book.category,
            page_count: updatedFields.page_count || book.page_count,
            publisher: truncateField(updatedFields.publisher || book.publisher, b.publisher),
          };
        }
        return book;
      });
    });
    setFetchResults(null)
  }

  const saveSelectedBooks = async () => {
    if (selectedIndices.size === 0) return

    setSavingBooks(true)
    const { data: { user } } = await supabase.auth.getUser()

    const booksToSave = detectedBooks.filter((_, i) => selectedIndices.has(i))

    for (const book of booksToSave) {
      const b = FIELD_LIMITS.books;
      await supabase
        .from('books')
        .insert({
          user_id: user.id,
          title: book.title,
          author: book.author || null,
          synopsis: truncateField(book.synopsis, b.synopsis) || null,
          description: truncateField(book.description, b.description) || null,
          page_count: book.page_count || null,
          publisher: truncateField(book.publisher, b.publisher) || null,
          series: truncateField(book.series, b.series) || null,
          series_number: book.series_number || null,
          location: truncateField(book.location, b.location) || null,
          status: book.status || 'owned',
          progress: book.progress || 'not_read',
          format: book.format || 'physical',

          notes: truncateField(book.notes, b.notes) || null,
          category: book.category || null,
          language: truncateField(book.language, b.language) || null
        })
    }

    // Remove saved books from the list
    const remainingBooks = detectedBooks.filter((_, i) => !selectedIndices.has(i))
    setDetectedBooks(remainingBooks)
    setSelectedIndices(new Set())

    // Re-check for duplicates with remaining books
    if (remainingBooks.length > 0) {
      await checkExistingBooks()
    }

    setSavingBooks(false)

    // Clear if no books remaining
    if (remainingBooks.length === 0) {
      setImagePreview(null)
    }
  }

  const clearAll = () => {
    setDetectedBooks([])
    setSelectedIndices(new Set())
    setExistingBooks([])
    setImagePreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const selectedCount = selectedIndices.size
  const allSelected = selectedCount === detectedBooks.length && detectedBooks.length > 0

  return (
    <div className={styles.scannerContainer}>
      <div className={`${styles.scannerHeader} animate-fade-in-up`}>
        <h2 className={styles.scannerTitle}>
          <span className={styles.titleIcon}>📷</span>
          {t('scanner.title')}
        </h2>
        <p className={styles.scannerSubtitle}>
          {t('scanner.subtitle')}
        </p>
      </div>

      <button
        type="button"
        className={`${styles.uploadZone} animate-fade-in-up stagger-1 ${isDragging ? styles.dragging : ''} ${imagePreview ? styles.hasPreview : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !scanning && fileInputRef.current?.click()}
        disabled={scanning}
        aria-label={t('scanner.dropZone.image.ariaLabel')}
        data-testid="scanner-upload-zone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          disabled={scanning}
          className={styles.uploadInput}
        />

        {imagePreview ? (
          <div className={styles.previewContainer}>
            <img src={imagePreview} alt="Preview" className={styles.imagePreview} />
            {!scanning && (
              <div
                className={`${styles.previewChange} btn-secondary`}
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation()
                  clearAll()
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    clearAll()
                  }
                }}
                aria-label={t('scanner.dropZone.image.change')}
              >
                {t('scanner.dropZone.image.change')}
              </div>
            )}
          </div>
        ) : (
          <div className={styles.uploadContent}>
            <div className={styles.uploadIconWrapper}>
              <span className={styles.uploadIcon}>📤</span>
            </div>
            <p className={styles.uploadText}>
              <strong>{t('scanner.dropZone.image.text')}</strong>
            </p>
            <p className={styles.uploadHint}>
              {t('scanner.dropZone.image.hint')}
            </p>
          </div>
        )}
      </button>

      <div className={`${styles.importDivider} animate-fade-in-up stagger-2`}>
        <span>{t('scanner.dropZone.list.divider')}</span>
      </div>

      <button
        type="button"
        className={`${styles.uploadZone} ${styles.importZone} animate-fade-in-up stagger-3 ${isDraggingList ? styles.dragging : ''}`}
        onDragOver={handleListDragOver}
        onDragLeave={handleListDragLeave}
        onDrop={handleListDrop}
        onClick={() => !scanning && listFileInputRef.current?.click()}
        disabled={scanning}
        aria-label={t('scanner.dropZone.list.ariaLabel')}
        data-testid="scanner-import-zone"
      >
        <input
          ref={listFileInputRef}
          type="file"
          accept=".csv,.json"
          onChange={handleListFileUpload}
          disabled={scanning}
          className={styles.uploadInput}
        />

        <div className={styles.uploadContent}>
          <div className={`${styles.uploadIconWrapper} ${styles.listIconWrapper}`}>
            <span className={styles.uploadIcon}>📄</span>
          </div>
          <p className={styles.uploadText}>
            <strong>{t('scanner.dropZone.list.text')}</strong>
          </p>
          <p className={styles.uploadHint}>
            {t('scanner.dropZone.list.hint')}
          </p>
        </div>
      </button>

      {scanning && (
        <div className={`${styles.scanningIndicator} animate-fade-in`}>
          <div className={styles.scanningAnimation}>
            <div className={styles.scanningLine}></div>
          </div>
          <div className={styles.scanningContent}>
            <div className={styles.loadingDots}>
              <span></span>
              <span></span>
              <span></span>
            </div>
            <p className={styles.scanningText}>{t('scanner.status.analyzing')}</p>
          </div>
        </div>
      )
      }

      {
        checkingDuplicates && !scanning && (
          <div className={`${styles.checkingIndicator} animate-fade-in`}>
            <div className={`${styles.spinner} ${styles.small}`}></div>
            <p className={styles.checkingText}>{t('scanner.status.verifying')}</p>
          </div>
        )
      }

      {
        detectedBooks.length > 0 && (
          <div className={`${styles.resultsSection} animate-fade-in-up`}>
            <div className={styles.resultsHeader}>
              <h3 className={styles.resultsTitle}>
                <span className={styles.resultsIcon}>✨</span>
                {t('scanner.results.title')}
                <span className={styles.resultsCount}>{detectedBooks.length}</span>
              </h3>
              <div className={styles.resultsActions}>
                <button
                  className="btn-success"
                  onClick={saveSelectedBooks}
                  disabled={selectedCount === 0 || savingBooks}
                >
                  {savingBooks ? (
                    <>
                      <span className={`${styles.spinner} ${styles.small}`}></span>
                      {t('scanner.status.saving')}
                    </>
                  ) : (
                    <>
                      <span>💾</span>
                      {t('scanner.results.addSelected', { count: selectedCount })}
                    </>
                  )}
                </button>
                <button
                  className="btn-ghost"
                  onClick={clearAll}
                >
                  {t('scanner.results.clear')}
                </button>
              </div>
            </div>

            {/* Selection controls */}
            <div className={styles.selectionControls}>
              <button
                className={styles.btnLink}
                onClick={allSelected ? deselectAll : selectAll}
              >
                {allSelected ? `✓ ${t('scanner.results.deselectAll')}` : `☐ ${t('scanner.results.selectAll')}`}
              </button>

              {/* Fetch Info Button */}
              <button
                className={`btn-secondary ${styles.btnSmall}`}
                onClick={handleFetchInfo}
                disabled={selectedCount === 0 || fetchingInfo}
                title="Buscar información (descripción, categoría, pág.) en Google Books"
              >
                {fetchingInfo ? (
                  <>
                    <span className={`${styles.spinner} ${styles.small}`}></span>
                    {t('scanner.status.searching')}
                  </>
                ) : (
                  <>
                    <span>🌐</span>
                    {t('scanner.results.fetchInfo')}
                  </>
                )}
              </button>

              {/* Bulk Edit Button */}
              <button
                className={`btn-secondary ${styles.btnSmall}`}
                onClick={() => setShowBulkEditModal(true)}
                disabled={selectedCount === 0}
              >
                <span>✏️</span>
                {t('scanner.results.bulkEdit', { count: selectedCount })}
              </button>

              {selectedCount > 0 && (
                <>
                  <span className={styles.selectionCount}>
                    {t('scanner.results.selectedCount', { count: selectedCount, total: detectedBooks.length })}
                  </span>
                  <button
                    className={`${styles.bulkRemoveBtn} btn-danger`}
                    onClick={handleBulkRemove}
                  >
                    🗑️ {t('scanner.results.deleteSelection')}
                  </button>
                </>
              )}
            </div>

            <div className={styles.booksGrid}>
              {detectedBooks.map((book, index) => {
                const isSelected = selectedIndices.has(index)
                const matchStatus = getBookMatchStatus(book)
                const isDuplicate = matchStatus === 'exact'
                const isPartialMatch = matchStatus === 'partial'
                let matchClass = ''
                if (isDuplicate) matchClass = styles.isDuplicate
                else if (isPartialMatch) matchClass = styles.isPartialMatch

                return (
                  <article
                    key={`${book.title}-${index}`}
                    className={`${styles.detectedBookCard} animate-fade-in-up stagger-${Math.min(index + 1, 5)} ${isSelected ? styles.isSelected : ''} ${styles.clickable} ${matchClass}`}
                    onClick={() => handleScannedBookClick(book, index)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleScannedBookClick(book, index);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Editar detalles de ${book.title}`}
                  >
                    <label className={styles.bookCheckboxWrapper} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => toggleBookSelection(e, index)}
                        className={styles.bookCheckboxInput}
                      />
                      <span className={styles.bookCheckbox}></span>
                    </label>

                    <button
                      className={styles.scannedBookDelete}
                      onClick={(e) => {
                        e.stopPropagation()
                        removeScannedBook(index)
                      }}
                      aria-label={t('scanner.bookCard.delete')}
                    >
                      ✕
                    </button>

                    <div className="book-cover">
                      <span className="book-emoji-large">📖</span>
                    </div>
                    <div className={styles.bookDetails}>
                      {/* Editable Title */}
                      <input
                        type="text"
                        value={book.title}
                        onChange={(e) => updateBookField(index, 'title', e.target.value)}
                        className={`${styles.bookInput} ${styles.bookInputTitle}`}
                        placeholder={t('scanner.bookCard.titlePlaceholder')}
                        onClick={(e) => e.stopPropagation()} // Prevent opening modal when editing
                      />
                      {/* Editable Author */}
                      <input
                        type="text"
                        value={book.author || ''}
                        onChange={(e) => updateBookField(index, 'author', e.target.value)}
                        className={`${styles.bookInput} ${styles.bookInputAuthor}`}
                        placeholder={t('scanner.bookCard.authorPlaceholder')}
                        onClick={(e) => e.stopPropagation()} // Prevent opening modal when editing
                      />
                    </div>

                    {/* Match status badges - clickable */}
                    {matchStatus === 'exact' && (
                      <button
                        className={`${styles.duplicateBadge} ${styles.clickableBadge}`}
                        title="Ver libro en biblioteca"
                        onClick={(e) => {
                          e.stopPropagation()
                          setMatchPreviewBook(getMatchingBook(book))
                        }}
                      >
                        <span className={styles.duplicateIcon}>✓</span>
                        <span className={styles.duplicateText}>{t('scanner.bookCard.duplicate')}</span>
                      </button>
                    )}
                    {matchStatus === 'partial' && (
                      <button
                        className={`${styles.partialMatchBadge} ${styles.clickableBadge}`}
                        title="Ver libro similar"
                        onClick={(e) => {
                          e.stopPropagation()
                          setMatchPreviewBook(getMatchingBook(book))
                        }}
                      >
                        <span className={styles.partialMatchIcon}>❓</span>
                        <span className={styles.partialMatchText}>{t('scanner.bookCard.partial')}</span>
                      </button>
                    )}
                  </article>
                )
              })}
            </div>
          </div>
        )
      }

      {
        !scanning && detectedBooks.length === 0 && imagePreview && (
          <div className={`${styles.emptyResults} animate-fade-in`}>
            <span className={styles.emptyIcon}>🔍</span>
            <p className={styles.emptyText}>{t('scanner.results.empty')}</p>
            <p className={styles.emptyHint}>{t('scanner.results.emptyHint')}</p>
          </div>
        )
      }

      {/* Bulk Edit Modal */}
      {
        showBulkEditModal && (
          <BulkEditModal
            selectedCount={selectedCount}
            onClose={() => setShowBulkEditModal(false)}
            onApply={applyBulkEdit}
            existingAuthors={existingAuthors}
            existingPublishers={existingPublishers}
            existingLocations={existingLocations}
            existingSeries={existingSeries}
            existingCategories={existingCategories}
            existingLanguages={existingLanguages}
          />
        )
      }

      {/* Match Preview Modal */}
      {
        matchPreviewBook && (
          <div className={styles.modalOverlay} onClick={() => setMatchPreviewBook(null)}>
            <div className={`${styles.modalContent} ${styles.matchPreviewModal}`} onClick={(e) => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>
                  <span>📚</span>
                  {t('scanner.matchModal.title')}
                </h3>
                <button
                  className={styles.modalClose}
                  onClick={() => setMatchPreviewBook(null)}
                  aria-label={t('scanner.matchModal.close')}
                >
                  ✕
                </button>
              </div>
              <div className={`${styles.modalBody} ${styles.matchPreviewBody}`}>
                <div className={styles.matchPreviewField}>
                  <span className={styles.matchPreviewLabel}>📖 {t('scanner.matchModal.fields.title')}</span>
                  <span className={styles.matchPreviewValue}>{matchPreviewBook.title}</span>
                </div>
                <div className={styles.matchPreviewField}>
                  <span className={styles.matchPreviewLabel}>✍️ {t('scanner.matchModal.fields.author')}</span>
                  <span className={styles.matchPreviewValue}>{matchPreviewBook.author || 'Desconocido'}</span>
                </div>
                {matchPreviewBook.publisher && (
                  <div className={styles.matchPreviewField}>
                    <span className={styles.matchPreviewLabel}>🏢 {t('scanner.matchModal.fields.publisher')}</span>
                    <span className={styles.matchPreviewValue}>{matchPreviewBook.publisher}</span>
                  </div>
                )}
                {matchPreviewBook.series && (
                  <div className={styles.matchPreviewField}>
                    <span className={styles.matchPreviewLabel}>📚 {t('scanner.matchModal.fields.series')}</span>
                    <span className={styles.matchPreviewValue}>
                      {matchPreviewBook.series}
                      {!!matchPreviewBook.series_number && ` #${matchPreviewBook.series_number}`}
                    </span>
                  </div>
                )}
                {matchPreviewBook.location && (
                  <div className={styles.matchPreviewField}>
                    <span className={styles.matchPreviewLabel}>📍 {t('scanner.matchModal.fields.location')}</span>
                    <span className={styles.matchPreviewValue}>{matchPreviewBook.location}</span>
                  </div>
                )}
                <div className={styles.matchPreviewRow}>
                  {matchPreviewBook.status && (
                    <div className={styles.matchPreviewField}>
                      <span className={styles.matchPreviewLabel}>🏷️ {t('scanner.matchModal.fields.status')}</span>
                      <span className={styles.matchPreviewValue}>{matchPreviewBook.status}</span>
                    </div>
                  )}
                  {matchPreviewBook.progress && (
                    <div className={styles.matchPreviewField}>
                      <span className={styles.matchPreviewLabel}>📊 {t('scanner.matchModal.fields.progress')}</span>
                      <span className={styles.matchPreviewValue}>{matchPreviewBook.progress}</span>
                    </div>
                  )}
                </div>
                {matchPreviewBook.notes && (
                  <div className={styles.matchPreviewField}>
                    <span className={styles.matchPreviewLabel}>📝 {t('scanner.matchModal.fields.notes')}</span>
                    <span className={styles.matchPreviewValue}>{matchPreviewBook.notes}</span>
                  </div>
                )}
                <div className={styles.matchPreviewRow}>
                  <span className={styles.matchPreviewLabel}>{t('scanner.matchModal.fields.added')}:</span>
                  <span className={styles.matchPreviewValue}>
                    {new Date(matchPreviewBook.created_at).toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </span>
                </div>
              </div>
              <div className={styles.modalFooter}>
                <button className="btn-primary" onClick={() => setMatchPreviewBook(null)}>
                  {t('scanner.matchModal.close')}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Fetch Review Modal */}
      {
        fetchResults && (
          <FetchReviewModal
            results={fetchResults as unknown as FetchReviewResult[]}
            onClose={() => setFetchResults(null)}
            onApply={handleApplyFetchResults}
          />
        )
      }

      {/* Scanned Book Detail Modal (Individual Save) */}
      {
        editingScannedBook && (
          <ScannedBookDetailModal
            book={editingScannedBook as unknown as Partial<Book>}
            onClose={() => setEditingScannedBook(null)}
            onSave={handleSaveIndividualScannedBook}
            onUpdate={handleUpdateScannedBook}
            existingAuthors={existingAuthors}
            existingPublishers={existingPublishers}
            existingLocations={existingLocations}
            existingSeries={existingSeries}
            existingCategories={existingCategories}
            existingLanguages={existingLanguages}
          />
        )
      }

      <CustomConfirmModal
        isOpen={notification.isOpen}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        showCancel={false}
        confirmText={t('bookList.modals.deleteBook.understood', { defaultValue: 'Entendido' })} 
        onConfirm={() => setNotification({ ...notification, isOpen: false })}
      />
    </div >
  )
}
